import React, { useEffect, useRef } from 'react';
import { Button, DotLoading, Input, NavBar } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomerServiceViewState } from './types';
import { customerServicePageStyles } from './styles';

interface CustomerServiceViewProps {
  state: CustomerServiceViewState;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onRefresh: () => void;
}

function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export const CustomerServiceView: React.FC<CustomerServiceViewProps> = ({ state, onDraftChange, onSend, onRefresh }) => {
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation(['customerService', 'common']);

  // Resolve sender display name based on role.
  const getSenderDisplayName = (senderRole: string): string => {
    return senderRole === 'promoter' ? t('senderMe') : t('senderAgent');
  };

  useEffect(() => {
    const element = messageListRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [state.messages.length]);

  return (
    <div style={customerServicePageStyles.page}>
      <div style={customerServicePageStyles.header}>
        <NavBar
          onBack={() => navigate(-1)}
          right={<Button size="mini" fill="none" onClick={onRefresh}>{t('common:refresh')}</Button>}
        >
          {t('navTitle')}
        </NavBar>
      </div>

      <div style={customerServicePageStyles.chatCard}>
        <div style={customerServicePageStyles.intro}>
          <span style={customerServicePageStyles.onlineDot} />
          <span>{t('online')}</span>
        </div>

        {state.loading ? (
          <div style={customerServicePageStyles.loading}>
            <DotLoading />
          </div>
        ) : (
          <div ref={messageListRef} style={customerServicePageStyles.messageList}>
            {state.messages.length === 0 ? (
              <div style={customerServicePageStyles.emptyState}>
                <div className="webview-subtitle" style={{ textAlign: 'center' }}>
                  {t('empty')}
                </div>
              </div>
            ) : (
              state.messages.map((item) => {
                const isPromoter = item.sender_role === 'promoter';
                return (
                  <div
                    key={item.id}
                    style={{
                      ...customerServicePageStyles.messageRow,
                      justifyContent: isPromoter ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div style={customerServicePageStyles.messageContent}>
                      <div
                        className="webview-micro"
                        style={{
                          ...customerServicePageStyles.messageMeta,
                          textAlign: isPromoter ? 'right' : 'left',
                        }}
                      >
                        {getSenderDisplayName(item.sender_role)} · {formatTime(item.created_at)}
                      </div>
                      <div
                        style={{
                          ...customerServicePageStyles.messageBubble,
                          background: isPromoter ? '#1769ff' : 'rgba(242, 246, 255, 0.96)',
                          color: isPromoter ? '#ffffff' : 'var(--webview-text)',
                          borderBottomRightRadius: isPromoter ? 6 : 18,
                          borderBottomLeftRadius: isPromoter ? 18 : 6,
                        }}
                      >
                        {item.content}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        <div style={customerServicePageStyles.composerShell}>
          <div style={customerServicePageStyles.inputWrap}>
            <Input
              value={state.messageDraft}
              placeholder={t('placeholder')}
              onChange={onDraftChange}
              onEnterPress={onSend}
              style={customerServicePageStyles.input}
            />
          </div>
          <Button
            color="primary"
            loading={state.sending}
            disabled={!state.messageDraft.trim()}
            onClick={onSend}
            style={customerServicePageStyles.sendButton}
          >
            {t('send')}
          </Button>
        </div>
      </div>
    </div>
  );
};
